/**
 * Core Layer - Business Failures
 * Domain-specific failure types for business logic
 *
 * NOTE: These are technical failure classes, not user-facing messages.
 * User-facing messages should come from translation files (messages/*.json)
 */

/**
 * Base failure class for business logic failures
 */
export abstract class Failure {
  constructor(public readonly message: string) {}
}

/**
 * Failure when a required resource is not found
 */
export class NotFoundFailure extends Failure {
  constructor(resource: string) {
    super(`${resource}_not_found`);
  }
}

/**
 * Failure when user is not authorized to perform an action
 */
export class UnauthorizedFailure extends Failure {
  constructor(message?: string) {
    super(message ?? "unauthorized_action");
  }
}

/**
 * Failure when input validation fails
 */
export class ValidationFailure extends Failure {
  constructor(
    message: string,
    public readonly errors?: Record<string, string[]>
  ) {
    super(message);
  }
}

/**
 * Failure when a network operation fails
 */
export class NetworkFailure extends Failure {
  constructor(message?: string) {
    super(message ?? "network_operation_failed");
  }
}

/**
 * Failure when a server operation fails
 */
export class ServerFailure extends Failure {
  constructor(message?: string) {
    super(message ?? "server_operation_failed");
  }
}

/**
 * Failure when a cache operation fails
 */
export class CacheFailure extends Failure {
  constructor(message?: string) {
    super(message ?? "cache_operation_failed");
  }
}

/**
 * Generic unexpected failure
 */
export class UnexpectedFailure extends Failure {
  constructor(message?: string) {
    super(message ?? "unexpected_error");
  }
}
